"use client";

import { useState } from "react";
import Image from "next/image";
import { signOut, useSession } from "next-auth/react";
import { useDispatch } from "react-redux";

import { clearBasket } from "@/features/basket/basketSlice";
import { removeSavedLoginAccount } from "@/lib/auth/saved-accounts";
import { markLoggingOut } from "@/lib/auth/logout-guard";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import logoutImage from "/public/assets/profile/logout/logout-cover.png";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

export default function LogoutModal({ open, onOpenChange }: Props) {
	const dispatch = useDispatch();
	const { data: session } = useSession();
	const [isSigningOut, setIsSigningOut] = useState(false);

	const handleLogout = async () => {
		setIsSigningOut(true);
		markLoggingOut();
		dispatch(clearBasket());
		if (session?.user?.id) {
			removeSavedLoginAccount(session.user.id);
		}
		await signOut({ redirect: true, callbackUrl: "/" });
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md p-8">
				<DialogHeader className="space-y-4 text-center">
					<DialogTitle className="text-2xl font-semibold text-foreground sm:text-3xl">
						Come back <span className="text-tertiary">soon!</span>
					</DialogTitle>
					<DialogDescription className="font-light text-muted-foreground">
						Are you sure you want to log out?
					</DialogDescription>
				</DialogHeader>

				<Image
					src={logoutImage}
					alt=""
					aria-hidden
					sizes="50vw"
					quality={100}
					draggable="false"
					loading="eager"
					className="mx-auto my-4 max-w-xs w-auto h-auto"
				/>

				<div className="flex w-full flex-col justify-center gap-4 sm:flex-row max-w-sm mx-auto">
					<Button
						onClick={handleLogout}
						aria-label="Confirm logout"
						className="custom-box-shadow h-12 w-full bg-grey/50 hover:bg-grey/25 dark:border dark:border-secondary/20 dark:bg-card dark:text-foreground dark:hover:bg-card/80"
						disabled={isSigningOut}
					>
						{isSigningOut ? "Logging out..." : "Yes"}
					</Button>
					<Button
						variant="accent"
						className="custom-box-shadow h-12 w-full"
						disabled={isSigningOut}
						onClick={() => onOpenChange(false)}
					>
						No
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
